import React from "react";
import * as Popover from "@radix-ui/react-popover";
import styled from "styled-components";

// Anchor (not Trigger) so callers keep full control of their button's onClick:
// pen-click selects-tool-or-opens-popover, Marc-click toggles-visibility-or-
// opens-opacity, etc. Trigger would call onOpenChange on every click and
// double-toggle our externally-controlled state. Open state is therefore fully
// owned by the caller; Radix only ever calls onOpenChange(false) (Escape,
// click-outside).
function ToolPopover({
  open,
  onOpenChange,
  trigger,
  side = "top",
  align = "start",
  sideOffset = 8,
  autoFocusOnOpen = false,
  // While true, swallow outside-pointerdown so the popover doesn't
  // close. Used by ColorEditor's eyedropper session — clicking the marc
  // photo to pick a color must commit AND keep the editor open.
  lockOpen = false,
  children,
}) {
  // Ref the trigger DOM node so the outside-pointer-down handler can detect
  // clicks on our own anchor and skip closing — otherwise Radix closes on
  // pointerdown and the anchor's onClick reopens on the same gesture.
  const anchorRef = React.useRef(null);
  const triggerWithRef = React.cloneElement(trigger, { ref: anchorRef });

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor asChild>{triggerWithRef}</Popover.Anchor>
      <Popover.Portal>
        <Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          onOpenAutoFocus={(e) => {
            // Default: don't steal focus on open — popovers are usually opened
            // from a click on the canvas-adjacent toolbar and the user wants
            // their pointer/typing focus to stay where it was. Hotkey-opened
            // popovers opt in so the slider thumb gets focus and arrow keys
            // can drive it without an extra click.
            if (!autoFocusOnOpen) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            const target = e.detail.originalEvent.target;
            if (anchorRef.current && anchorRef.current.contains(target)) {
              e.preventDefault();
              return;
            }
            if (lockOpen) e.preventDefault();
          }}
        >
          <ContentInner>{children}</ContentInner>
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// onOpenAutoFocus prevented: tool hotkeys (q/w/e/1-6/m) are global and would
// be eaten by an auto-focused range input inside the popover.

const Content = styled(Popover.Content)`
  min-width: 220px;
  padding: 4px;
  background: var(--color-surface);
  /* border: 1px solid var(--color-border); */
  border-radius: 6px;
  box-shadow: var(--surface-box-shadow-strong);
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 20;
`;

const ContentInner = styled.div`
  border: 1px solid var(--color-orange-300);
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default ToolPopover;
